<template>
    <Head :title="category.name" />

    <div class="py-6">
        <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <!-- Header with Actions -->
            <div class="mb-6 flex flex-col sm:flex-row sm:items-center sm:justify-between">
                <div class="mb-4 sm:mb-0">
                    <Link
                        :href="route('settings.maintenance.asset-categories.index')"
                        class="inline-flex items-center text-sm text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300 mb-2"
                    >
                        <svg class="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7" />
                        </svg>
                        Back to Asset Categories
                    </Link>
                    <div class="flex items-center space-x-3">
                        <h1 class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ category.name }}</h1>
                        <InfoButton title="Asset Category Details" variant="default">
                            <p>
                                <strong class="text-secondary-900 dark:text-white">About this page</strong><br>
                                The Asset Category Details page shows comprehensive information about a single category including
                                its position in the hierarchy, child categories, and assigned assets. Use this page to manage
                                category relationships, view statistics, and edit category properties. Categories help organize
                                assets for better filtering, reporting, and maintenance planning.
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">How to use</strong><br>
                                • <strong>Quick Stats:</strong> View Direct Assets (assets directly in this category), Total
                                  Assets (includes assets in child categories), Child Categories count, and All Descendants
                                  (total subcategories below this one)<br>
                                • <strong>Edit Category:</strong> Update name, description, parent category, or active status<br>
                                • <strong>Activate/Deactivate:</strong> Toggle category availability without deleting it. Inactive
                                  categories don't appear in asset creation dropdowns<br>
                                • <strong>Delete:</strong> Remove category if it has no assets and no child categories. System
                                  prevents accidental deletion of categories in use<br>
                                • <strong>Child Categories Section:</strong> Shows direct subcategories. Click any child to view
                                  its details<br>
                                • <strong>Assets Section:</strong> Lists assets directly assigned to this category. Click "View
                                  all" to see complete list filtered by category<br>
                                • <strong>Parent Link:</strong> Click parent category name to navigate up the hierarchy
                            </p>
                            <p>
                                <strong class="text-secondary-900 dark:text-white">Tips</strong><br>
                                • <strong>Direct vs. Total Assets</strong> - Direct assets are assigned to this specific category;
                                  total includes assets in all child categories<br>
                                • <strong>Hierarchies enable reporting</strong> - Group by parent categories to get rollup
                                  reports across multiple asset types<br>
                                • <strong>Cannot delete if in use</strong> - Move or reassign assets and subcategories before
                                  deleting<br>
                                • <strong>Deactivate instead of delete</strong> - Keeps historical data intact while removing
                                  from active selection lists<br>
                                • <strong>Child categories</strong> inherit organizational context from parents for easier
                                  navigation<br>
                                • <strong>Descendants count</strong> shows the total depth of the category tree below this level
                            </p>
                        </InfoButton>
                        <span
                            :class="[
                                'px-2.5 py-1 text-xs font-medium rounded-full',
                                category.is_active
                                    ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                                    : 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-400'
                            ]"
                        >
                            {{ category.is_active ? 'Active' : 'Inactive' }}
                        </span>
                    </div>
                    <p v-if="category.parent" class="mt-1 text-sm text-gray-600 dark:text-gray-400 dark:text-secondary-500">
                        Parent: {{ category.parent.name }}
                    </p>
                </div>

                <div class="flex items-center space-x-3">
                    <button
                        @click="toggleActive"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        {{ category.is_active ? 'Deactivate' : 'Activate' }}
                    </button>
                    <button
                        @click="openEditModal"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                    >
                        Edit Category
                    </button>
                    <button
                        v-if="canDelete"
                        @click="showDeleteModal = true"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                    >
                        Delete
                    </button>
                    <div v-else class="relative group">
                        <button
                            disabled
                            class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-gray-400 dark:bg-gray-600 dark:bg-gray-700 cursor-not-allowed opacity-60"
                        >
                            Delete
                        </button>
                        <div class="absolute bottom-full right-0 mb-2 w-64 p-2 bg-gray-900 text-white text-xs rounded-md opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                            Cannot delete: {{ deleteReasons.join(', ') }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Statistics Cards -->
            <div class="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-blue-100 dark:bg-blue-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-blue-600 dark:text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Direct Assets</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ stats.direct_assets_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-green-100 dark:bg-green-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Total Assets</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ stats.total_assets_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-purple-100 dark:bg-purple-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-purple-600 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Child Categories</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ stats.children_count }}</p>
                        </div>
                    </div>
                </div>

                <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
                    <div class="flex items-center">
                        <div class="flex-shrink-0 bg-orange-100 dark:bg-orange-900/30 rounded-md p-3">
                            <svg class="h-6 w-6 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                        </div>
                        <div class="ml-4">
                            <p class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">All Descendants</p>
                            <p class="text-2xl font-semibold text-gray-900 dark:text-gray-100">{{ stats.descendants_count }}</p>
                        </div>
                    </div>
                </div>
            </div>

            <div class="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <!-- Main Information -->
                <div class="lg:col-span-2 space-y-6">
                    <!-- Basic Information -->
                    <div class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Basic Information</h2>
                            <dl class="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div>
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Name</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ category.name }}</dd>
                                </div>
                                <div v-if="category.parent">
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Parent Category</dt>
                                    <dd class="mt-1">
                                        <Link
                                            :href="route('settings.maintenance.asset-categories.show', category.parent.id)"
                                            class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                        >
                                            {{ category.parent.name }}
                                        </Link>
                                    </dd>
                                </div>
                                <div v-if="category.description" class="sm:col-span-2">
                                    <dt class="text-sm font-medium text-gray-500 dark:text-gray-400 dark:text-secondary-500">Description</dt>
                                    <dd class="mt-1 text-sm text-gray-900 dark:text-gray-100">{{ category.description }}</dd>
                                </div>
                            </dl>
                        </div>
                    </div>

                    <!-- Child Categories -->
                    <div v-if="category.children && category.children.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100 mb-4">Child Categories</h2>
                            <div class="space-y-2">
                                <Link
                                    v-for="child in category.children"
                                    :key="child.id"
                                    :href="route('settings.maintenance.asset-categories.show', child.id)"
                                    class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div>
                                        <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ child.name }}</p>
                                        <p class="text-xs text-gray-500 dark:text-gray-400 dark:text-secondary-500">{{ child.assets_count || 0 }} assets</p>
                                    </div>
                                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                        </div>
                    </div>

                    <!-- Assets in this Category -->
                    <div v-if="category.assets && category.assets.length > 0" class="bg-white dark:bg-gray-800 rounded-lg shadow">
                        <div class="p-6">
                            <div class="flex items-center justify-between mb-4">
                                <h2 class="text-lg font-medium text-gray-900 dark:text-gray-100">Assets in this Category</h2>
                                <span class="text-sm text-gray-500 dark:text-gray-400 dark:text-secondary-500">
                                    Showing {{ category.assets.length }} of {{ stats.direct_assets_count }}
                                </span>
                            </div>
                            <div class="space-y-2">
                                <Link
                                    v-for="asset in category.assets"
                                    :key="asset.id"
                                    :href="route('settings.maintenance.assets.show', asset.id)"
                                    class="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                >
                                    <div class="flex items-center space-x-3">
                                        <div class="w-10 h-10 rounded-lg bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0">
                                            <svg class="w-5 h-5 text-green-600 dark:text-green-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
                                            </svg>
                                        </div>
                                        <div>
                                            <p class="text-sm font-medium text-gray-900 dark:text-gray-100">{{ asset.name }}</p>
                                            <p class="text-xs text-gray-500 dark:text-gray-400 dark:text-secondary-500">
                                                {{ asset.asset_tag || 'No tag' }}
                                                <span v-if="asset.location"> • {{ asset.location.name }}</span>
                                            </p>
                                        </div>
                                    </div>
                                    <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                                    </svg>
                                </Link>
                            </div>
                            <div v-if="stats.direct_assets_count > category.assets.length" class="mt-4 text-center">
                                <Link
                                    :href="route('settings.maintenance.assets.index') + '?category=' + category.id"
                                    class="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                                >
                                    View all {{ stats.direct_assets_count }} assets →
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Sidebar -->
                <div class="space-y-6">
                    <!-- Future sidebar content can be added here -->
                </div>
            </div>
        </div>
    </div>

    <!-- Edit Modal -->
    <Teleport to="body">
        <Transition
            enter-active-class="transition ease-out duration-200"
            enter-from-class="opacity-0"
            enter-to-class="opacity-100"
            leave-active-class="transition ease-in duration-150"
            leave-from-class="opacity-100"
            leave-to-class="opacity-0"
        >
            <div
                v-if="showEditModal"
                class="fixed inset-0 z-50 overflow-y-auto"
                aria-labelledby="modal-title"
                role="dialog"
                aria-modal="true"
            >
                <!-- Background backdrop -->
                <div
                    class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
                    @click="closeEditModal"
                ></div>

                <!-- Modal panel -->
                <div class="flex min-h-full items-center justify-center p-4">
                    <div class="relative bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full">
                        <div class="p-6">
                            <div class="flex items-center justify-between mb-4">
                                <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Edit Asset Category</h3>
                                <button
                                    @click="closeEditModal"
                                    class="text-gray-400 hover:text-gray-500"
                                >
                                    <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                                    </svg>
                                </button>
                            </div>

                            <form @submit.prevent="submitEdit" class="space-y-4">
                                <!-- Name -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Name <span class="text-red-500">*</span>
                                    </label>
                                    <input
                                        v-model="editForm.name"
                                        type="text"
                                        required
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <!-- Description -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Description
                                    </label>
                                    <textarea
                                        v-model="editForm.description"
                                        rows="3"
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    />
                                </div>

                                <!-- Parent Category -->
                                <div>
                                    <label class="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                                        Parent Category
                                    </label>
                                    <select
                                        v-model="editForm.parent_id"
                                        class="w-full rounded-md border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-gray-100 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                                    >
                                        <option :value="null">None (Root Category)</option>
                                        <option v-for="cat in allCategories" :key="cat.id" :value="cat.id">
                                            {{ cat.path_name }}
                                        </option>
                                    </select>
                                </div>

                                <!-- Active Status -->
                                <div class="flex items-center">
                                    <input
                                        id="edit_is_active"
                                        v-model="editForm.is_active"
                                        type="checkbox"
                                        class="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 dark:border-gray-600 rounded"
                                    />
                                    <label for="edit_is_active" class="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                                        Active
                                    </label>
                                </div>

                                <!-- Actions -->
                                <div class="mt-6 flex justify-end space-x-3">
                                    <button
                                        type="button"
                                        @click="closeEditModal"
                                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        type="submit"
                                        :disabled="submitting"
                                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        <span v-if="submitting">Saving...</span>
                                        <span v-else>Save Changes</span>
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>

    <!-- Delete Confirmation Modal -->
    <div
        v-if="showDeleteModal"
        class="fixed inset-0 bg-gray-500 bg-opacity-75 flex items-center justify-center z-50"
        @click.self="showDeleteModal = false"
    >
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-md w-full mx-4">
            <div class="p-6">
                <div class="flex items-center mb-4">
                    <div class="flex-shrink-0 w-12 h-12 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center">
                        <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                        </svg>
                    </div>
                    <div class="ml-4">
                        <h3 class="text-lg font-medium text-gray-900 dark:text-gray-100">Delete Category</h3>
                    </div>
                </div>
                <p class="text-sm text-gray-600 dark:text-gray-400 mb-6">
                    Are you sure you want to delete "{{ category.name }}"? This action cannot be undone.
                </p>
                <div class="flex justify-end space-x-3">
                    <button
                        @click="showDeleteModal = false"
                        class="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 dark:hover:bg-gray-600"
                    >
                        Cancel
                    </button>
                    <button
                        @click="deleteCategory"
                        :disabled="deleting"
                        class="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        <span v-if="deleting">Deleting...</span>
                        <span v-else">Delete</span>
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, Transition, Teleport } from 'vue';
import { Head, Link, router } from '@inertiajs/vue3';
import route from '@/Utils/route';
import InfoButton from '@/Components/Common/InfoButton.vue';

const props = defineProps({
    category: Object,
    stats: Object,
    canDelete: Boolean,
    deleteReasons: Array,
    allCategories: Array,
});

const showDeleteModal = ref(false);
const deleting = ref(false);
const showEditModal = ref(false);
const submitting = ref(false);
const editForm = ref({
    name: '',
    description: '',
    parent_id: null,
    icon: '',
    is_active: true,
});

const toggleActive = () => {
    router.post(route('settings.maintenance.asset-categories.toggleActive', props.category.id), {}, {
        preserveScroll: true,
    });
};

const openEditModal = () => {
    editForm.value = {
        id: props.category.id,
        name: props.category.name,
        description: props.category.description || '',
        parent_id: props.category.parent_id,
        icon: props.category.icon || '',
        is_active: props.category.is_active,
    };
    showEditModal.value = true;
};

const closeEditModal = () => {
    showEditModal.value = false;
};

const submitEdit = () => {
    submitting.value = true;

    router.put(
        route('settings.maintenance.asset-categories.update', props.category.id),
        editForm.value,
        {
            preserveScroll: true,
            onSuccess: () => {
                closeEditModal();
            },
            onFinish: () => {
                submitting.value = false;
            },
        }
    );
};

const deleteCategory = () => {
    deleting.value = true;
    router.delete(route('settings.maintenance.asset-categories.destroy', props.category.id), {
        onFinish: () => {
            deleting.value = false;
            showDeleteModal.value = false;
        },
    });
};
</script>
